/**
 * Доля хвоста публичного рендерера: вид `image_choice` (выбор картинкой).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе с плиткой `ImageChoiceTile` (своя память об ошибке загрузки). Метка `case 'image_choice'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderImageChoice(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React, { useEffect, useState } from 'react';
// WEB-1988: из отдельного модуля, а не из среза редактора — иначе страница
// покупателя тянет весь editorSlice со стором проектов и организаций.
import { getStylePackById } from '@/themes/stylePacks';
import {
  optionTextColorOverride,
  optionFontSizeOverride,
  optionBackgroundOverride,
} from '../optionRowStyle';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { AuthorHintBox } from '../AuthorHintBox';
import { aiFields } from '../aiShapedFields';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

/**
 * WEB-2380 п.5 — КАРТИНКА ВАРИАНТА `image_choice`, КОТОРАЯ НЕ ЗАГРУЗИЛАСЬ.
 *
 * Адрес варианта мог быть выдуман моделью (сервер снимает такие гардом
 * `strip-invented-image-urls.util.ts`) или умереть со временем. Без
 * обработчика ошибки посетитель получал дыру в сетке выбора. Заглушка — та
 * же, что у плиток галереи: отдельный компонент нужен по той же причине
 * (состояние «не загрузилась» у каждой плитки своё, а хук внутри `map`
 * поставить нельзя).
 */
function ImageChoiceTile({
  src,
  alt,
  style,
  errorText,
}: {
  src: string;
  alt: string;
  style: React.CSSProperties;
  errorText: string;
}): React.ReactElement {
  const [failed, setFailed] = useState(false);
  // Автор поправил ссылку — прежняя ошибка к новой картинке отношения не имеет.
  useEffect(() => {
    setFailed(false);
  }, [src]);
  if (failed) {
    return (
      <AuthorHintBox
        style={{
          background: 'var(--color-bg-surface, #fafafa)',
          minHeight: style.height,
          width: style.width,
        }}
      >
        {errorText}
      </AuthorHintBox>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      decoding="async"
      onError={() => setFailed(true)}
      style={style}
    />
  );
}

export function renderImageChoice(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const {
    block,
    varCtx,
    stylePackId,
    primaryColor,
    richText,
    hasText,
    optionDisplay,
    interp,
    values,
    setValue,
  } = ctx;
  const ai = aiFields(block);
  const question = block.content ?? ai.question;
  const options = block.imageChoiceOptions ?? ai.options ?? [];
  const multiple = block.imageChoiceMultiple ?? ai.multiple ?? false;
  const selectedIds = (values[block.id] ?? '').split(',').filter(Boolean);
  const toggle = (optId: string) => {
    if (multiple) {
      const next = selectedIds.includes(optId)
        ? selectedIds.filter((id) => id !== optId)
        : [...selectedIds, optId];
      setValue(block.id, next.join(','));
    } else {
      setValue(block.id, optId);
    }
  };
  return (
    <div data-testid="image-choice" style={{ marginBottom: 16 }}>
      {hasText(question) && (
        <p
          style={{
            fontWeight: 600,
            marginBottom: 12,
            color: primaryColor,
            // Дозаказ владельца продукта: px-размер вопроса — option-
            // подписи (fontSize:14 ниже) остаются нетронуты.
            ...(block.fontSize ? { fontSize: block.fontSize } : {}),
          }}
        >
          {richText(question)}
        </p>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 10,
        }}
      >
        {options.map((opt) => {
          const isSelected = multiple
            ? selectedIds.includes(opt.id)
            : selectedIds[0] === opt.id;
          // WEB-924: раскладка фото-опции из imagery.* стиль-пака.
          // 'none' у пака означает «фото-карточек в этом стиле нет» — если
          // блок всё же пришёл, безопасно падаем в прежний label-below.
          const imagery = stylePackId
            ? getStylePackById(stylePackId)?.imagery
            : undefined;
          const cardMode =
            imagery?.optionCard === 'label-overlay-bottom' ||
            imagery?.optionCard === 'label-overlay-top' ||
            imagery?.optionCard === 'icon-circle'
              ? imagery.optionCard
              : 'label-below';
          // Тон плашки: brand — ссылка на палитру; dark/taupe — нейтральные
          // константы рендерера (референсы GLOV/Dressly), не эмиссия пака.
          const overlayBg =
            imagery?.overlayTone === 'brand'
              ? 'var(--color-primary, #1677ff)'
              : imagery?.overlayTone === 'taupe'
                ? 'rgba(133, 117, 110, 0.88)'
                : 'rgba(0, 0, 0, 0.62)';
          const iconRadius =
            imagery?.frame === 'rounded'
              ? 'var(--radius-image, var(--border-radius-md, 8px))'
              : '50%';
          const isOverlay =
            cardMode === 'label-overlay-bottom' ||
            cardMode === 'label-overlay-top';
          // Возврат приёмки 02.09 (WEB-1673 + WEB-1672): подписи под
          // фотографиями не управлялись из панели НИЧЕМ — кегль стоял
          // числом (14), цвет делился с заголовком вопроса. Лечится тем
          // же средством, что у соседних блоков с вариантами: накладки
          // спредятся ПОСЛЕ прежних значений, поэтому слой автора
          // сильнее, а пустое поле спредит {} — разметка старой воронки
          // остаётся байт-в-байт прежней.
          //
          // Накладкам отдаются ТОЛЬКО поля текста, названные поимённо.
          // Две причины, обе намеренные: второй слой накладки цвета
          // (авто-контраст по заливке варианта, WEB-1674) здесь не при
          // чём — прямоугольника варианта у фото-карточек нет, и полей
          // его заливки схема этому типу не даёт; и сторож контракта
          // имён (field-name-contract.spec.ts) требует, чтобы поле схемы
          // читалось в ветке отрисовки ПОД СВОИМ ИМЕНЕМ — у блока с
          // двойной номенклатурой имён иначе не видно, доезжает ли
          // написанное генерацией до читателя.
          const optionLabelFields = {
            optionTextColor: block.optionTextColor,
            optionFontSize: block.optionFontSize,
            // Решение владельца №2 (2026-10-08): градиент выбранной
            // карточки — и заливка (ниже), и опора авто-контраста подписи.
            optionSelectedBackgroundGradient:
              block.optionSelectedBackgroundGradient,
          };
          const optionLabelStyle = {
            ...optionFontSizeOverride(optionLabelFields),
            ...optionTextColorOverride(optionLabelFields, isSelected),
          };
          return (
            <div
              key={opt.id}
              data-testid={`image-choice-option-${opt.id}`}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              onClick={() => toggle(opt.id)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') toggle(opt.id);
              }}
              style={{
                border: `2px solid ${isSelected ? 'var(--color-primary, #4094F7)' : 'var(--color-border-primary, #f0f0f0)'}`,
                borderRadius: 'var(--border-radius-lg, 12px)',
                overflow: 'hidden',
                cursor: 'pointer',
                textAlign: cardMode === 'icon-circle' ? 'left' : 'center',
                ...(isOverlay ? { position: 'relative' as const } : {}),
                ...(cardMode === 'icon-circle'
                  ? {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                    }
                  : {}),
                // Решение владельца №2: градиент выбранной карточки; пустое
                // поле спредит {} — вид прежний.
                ...optionBackgroundOverride(optionLabelFields, isSelected),
              }}
            >
              {opt.imageUrl && (
                <ImageChoiceTile
                  src={opt.imageUrl}
                  // WEB-2923: текст для чтения с экрана — тоже текст автора.
                  alt={interp(opt.label)}
                  // WEB-2380 п.5: адрес варианта мог быть выдуман
                  // моделью или умереть — вместо дыры в сетке выбора
                  // показываем ту же заглушку, что у галереи.
                  errorText={
                    getPublicQuizStrings(varCtx?.system?.locale).imageLoadError
                  }
                  style={
                    cardMode === 'icon-circle'
                      ? {
                          width: 48,
                          height: 48,
                          objectFit: 'cover',
                          display: 'block',
                          borderRadius: iconRadius,
                          flexShrink: 0,
                        }
                      : {
                          width: '100%',
                          height: 100,
                          objectFit: 'cover',
                          display: 'block',
                        }
                  }
                />
              )}
              <div
                data-testid={`image-choice-label-${opt.id}`}
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: isOverlay && opt.imageUrl ? '#fff' : primaryColor,
                  ...optionLabelStyle,
                  // WEB-936 п.3: длинная подпись вылезала за карточку —
                  // при оверлее плашка absolute росла от края и накрывала
                  // фото. Держим её в пределах карточки: перенос по любому
                  // месту (длинное слово/URL) + кламп строк с многоточием.
                  // minWidth:0 нужен icon-circle-раскладке (flex-ребёнок).
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: isOverlay && opt.imageUrl ? 2 : 3,
                  lineHeight: 1.3,
                  // Страховка для движков без -webkit-line-clamp: столько же
                  // строк по высоте (2 при оверлее, 3 под фото) + паддинги.
                  maxHeight:
                    isOverlay && opt.imageUrl
                      ? 'calc(2 * 1.3em + 12px)'
                      : 'calc(3 * 1.3em + 16px)',
                  overflow: 'hidden',
                  ...(isOverlay && opt.imageUrl
                    ? {
                        position: 'absolute' as const,
                        left: 0,
                        right: 0,
                        ...(cardMode === 'label-overlay-bottom'
                          ? { bottom: 0 }
                          : { top: 0 }),
                        background: overlayBg,
                        padding: '6px 10px',
                      }
                    : { padding: '8px 10px' }),
                }}
              >
                {optionDisplay(opt)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
